
import React, { useState } from 'react';
import { Shield, CheckCircle, QrCode, MessageSquare, BarChart3, Check, Loader2, ChevronRight, Sparkles, Users, Lock, Zap } from 'lucide-react';
import { ViewState, LegalTab , priceForSeats } from '../types';
import { PhoneMockup, BrowserMockup } from '../components/PhoneMockup';
import {
  DemoQRManagement,
  DemoWorkerHome,
  DemoWorkerInput,
  DemoAIAnalysis,
  DemoMiniDashboard,
  FAQAccordion
} from '../components/OnboardingDemos';
import { HeroDashboardMockup } from '../components/DashboardComponents';
import { DeckDownloadCTA } from '../components/DeckDownloadCTA';

// ステップタブの型定義
type OnboardingStep = 1 | 2 | 3 | 4;

// ステップ2のサブステップ
type WorkerSubStep = 'home' | 'input' | 'analysis';

export const GuideView: React.FC<{ onChangeView?: (v: ViewState) => void }> = ({ onChangeView }) => {
  const [activeStep, setActiveStep] = useState<OnboardingStep>(1);
  const [workerSubStep, setWorkerSubStep] = useState<WorkerSubStep>('home');

  const steps = [
    { id: 1 as const, label: 'QR発行', icon: QrCode },
    { id: 2 as const, label: '報告', icon: MessageSquare },
    { id: 3 as const, label: 'AI分析', icon: Sparkles },
    { id: 4 as const, label: '可視化', icon: BarChart3 },
  ];

  const faqItems = [
    {
      question: 'アプリのインストールは必要ですか？',
      answer: 'いいえ、不要です。安全ポスト＋はWebアプリケーションなので、スマートフォンの標準カメラでQRコードを読み取るだけで利用開始できます。iOS、Androidどちらでも利用可能です。'
    },
    {
      question: '報告者の名前はバレませんか？',
      answer: 'はい、完全匿名で報告できます。AIが報告内容から個人を特定できる情報（名前、所属、特徴的な表現など）を自動的に伏せ字にします。報告者情報はシステムにも保存されません。'
    },
    {
      question: '4M分析とは何ですか？',
      answer: '4M分析は、問題の原因を「Man（人的要因）」「Machine（設備・機械）」「Material（材料・環境）」「Method（作業方法）」の4つの観点から分類する手法です。安全ポスト＋ではAIが自動で4M分類を行い、効果的な対策立案を支援します。'
    },
    {
      question: '無料プランでどこまで使えますか？',
      answer: 'フリープランでは、QRコード3個まで、AI分析10回/月、音声入力5回/月が利用可能です。まずは1つの現場で試してみて、効果を実感してから有料プランへの移行をご検討ください。'
    },
    {
      question: '導入にどのくらい時間がかかりますか？',
      answer: '最短5分で導入完了です。アカウント登録後、QRコードを発行して印刷するだけ。複雑な設定やシステム連携は不要で、すぐに現場で使い始められます。'
    },
    {
      question: 'データはどこに保存されますか？',
      answer: 'すべてのデータは国内のセキュアなクラウドサーバー（Supabase）に保存されます。SSL暗号化通信、アクセス制御、定期バックアップにより、企業の機密情報を安全に管理します。'
    },
  ];

  return (
    <div className="py-12 md:py-20">
      {/* Hero Section */}
      <div className="text-center mb-12 px-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-safety-100 text-safety-700 rounded-full text-sm font-medium mb-6">
          <Zap size={16} />
          最短5分で導入完了
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-industrial-900 mb-4">
          安全ポスト＋ の使い方
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          導入から運用まで、たった4ステップ。<br className="hidden md:block" />
          実際の画面を見ながら、使い方を確認しましょう。
        </p>
      </div>

      {/* Step Navigation */}
      <div className="max-w-4xl mx-auto px-6 mb-12">
        <div className="flex justify-center">
          <div className="inline-flex bg-slate-100 rounded-2xl p-1.5 gap-1">
            {steps.map((step) => {
              const Icon = step.icon;
              const isActive = activeStep === step.id;
              return (
                <button
                  key={step.id}
                  onClick={() => {
                    setActiveStep(step.id);
                    if (step.id === 2) setWorkerSubStep('home');
                  }}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white text-safety-600 shadow-md'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={18} />
                  <span className="hidden md:inline">{step.label}</span>
                  <span className="md:hidden">{step.id}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6">
        {/* Step 1: QR発行 */}
        {activeStep === 1 && (
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            <div className="flex-1 order-2 lg:order-1">
              <div className="inline-block px-4 py-1 bg-safety-100 text-safety-700 rounded-full text-sm font-bold mb-4">
                STEP 1
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-industrial-900 mb-4">
                管理者: QRコードを発行する
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                管理者ダッシュボードから、現場やラインごとにQRコードを発行します。
                発行されたQRコードを印刷し、休憩所や危険箇所の近くなど、目につく場所に掲示してください。
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <CheckCircle className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">エリアごとに分けて発行すると分析精度が向上</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <CheckCircle className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">印刷用PDFはワンクリックでダウンロード可能</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <CheckCircle className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">フリープランでも3つまでQRコード作成可能</span>
                </li>
              </ul>
              <button
                onClick={() => setActiveStep(2)}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-safety-500 text-white rounded-xl font-bold hover:bg-safety-600 transition shadow-lg shadow-safety-500/30"
              >
                次のステップへ
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="flex-1 order-1 lg:order-2 flex justify-center">
              <PhoneMockup scale="lg">
                <DemoQRManagement />
              </PhoneMockup>
            </div>
          </div>
        )}

        {/* Step 2: 報告（サブステップあり） */}
        {activeStep === 2 && (
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            <div className="flex-1 order-2 lg:order-1">
              <div className="inline-block px-4 py-1 bg-safety-100 text-safety-700 rounded-full text-sm font-bold mb-4">
                STEP 2
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-industrial-900 mb-4">
                作業員: スマホでスキャンして報告
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                アプリのインストールは不要。標準カメラでQRコードを読み取るだけで、報告画面が立ち上がります。
                「ヒヤリとしたこと」「気づき」をテキスト、音声、写真で送信できます。
              </p>

              {/* Sub-step buttons */}
              <div className="flex gap-2 mb-6">
                {[
                  { id: 'home' as const, label: 'QRスキャン後' },
                  { id: 'input' as const, label: '報告入力' },
                  { id: 'analysis' as const, label: 'AI分析結果' },
                ].map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => setWorkerSubStep(sub.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      workerSubStep === sub.id
                        ? 'bg-safety-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <Lock className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">ログイン不要で誰でも利用可能</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <Users className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">AIが個人名を自動で伏せ字にするので安心</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <Sparkles className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">音声入力対応で素早く報告</span>
                </li>
              </ul>
              <button
                onClick={() => setActiveStep(3)}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-safety-500 text-white rounded-xl font-bold hover:bg-safety-600 transition shadow-lg shadow-safety-500/30"
              >
                次のステップへ
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="flex-1 order-1 lg:order-2 flex justify-center">
              <PhoneMockup scale="lg">
                {workerSubStep === 'home' && <DemoWorkerHome />}
                {workerSubStep === 'input' && <DemoWorkerInput />}
                {workerSubStep === 'analysis' && <DemoAIAnalysis />}
              </PhoneMockup>
            </div>
          </div>
        )}

        {/* Step 3: AI分析 */}
        {activeStep === 3 && (
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            <div className="flex-1 order-2 lg:order-1">
              <div className="inline-block px-4 py-1 bg-safety-100 text-safety-700 rounded-full text-sm font-bold mb-4">
                STEP 3
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-industrial-900 mb-4">
                AIが自動で分析・匿名化
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                報告内容はAIが即座に分析。危険度の判定、4M分類、改善提案まで自動で行います。
                個人を特定できる情報も自動的に伏せ字にされるので、安心して報告できます。
              </p>

              <div className="bg-slate-50 rounded-xl p-6 mb-6 border border-slate-200">
                <h3 className="font-bold text-slate-800 mb-4">AIが行う分析</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <span className="text-red-600 text-xs font-bold">High</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">危険度判定</p>
                      <p className="text-xs text-slate-500">High/Medium/Low</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 text-xs font-bold">4M</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">要因分類</p>
                      <p className="text-xs text-slate-500">人/機械/材料/方法</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Lock size={16} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">匿名化処理</p>
                      <p className="text-xs text-slate-500">個人情報を自動伏せ字</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Sparkles size={16} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">改善提案</p>
                      <p className="text-xs text-slate-500">具体的な対策を提示</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setActiveStep(4)}
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-safety-500 text-white rounded-xl font-bold hover:bg-safety-600 transition shadow-lg shadow-safety-500/30"
              >
                次のステップへ
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="flex-1 order-1 lg:order-2 flex justify-center">
              <PhoneMockup scale="lg">
                <DemoAIAnalysis />
              </PhoneMockup>
            </div>
          </div>
        )}

        {/* Step 4: 可視化 */}
        {activeStep === 4 && (
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            <div className="flex-1 order-2 lg:order-1">
              <div className="inline-block px-4 py-1 bg-safety-100 text-safety-700 rounded-full text-sm font-bold mb-4">
                STEP 4
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-industrial-900 mb-4">
                チーム: 安全状況を可視化・改善
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                集まったデータはリアルタイムにダッシュボードに反映されます。
                AIが危険度を判定し、優先すべき対策を提案。朝礼や安全大会で活用しましょう。
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <CheckCircle className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">4M（人・機械・材料・方法）で要因を自動分析</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <CheckCircle className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">危険度「High」の案件は即座にアラート</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-safety-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <CheckCircle className="text-safety-600" size={14} />
                  </div>
                  <span className="text-slate-700">週次・月次のトレンドを自動グラフ化</span>
                </li>
              </ul>
            </div>
            <div className="flex-1 order-1 lg:order-2">
              <BrowserMockup className="w-full max-w-lg mx-auto">
                <div className="h-80 overflow-hidden">
                  <HeroDashboardMockup />
                </div>
              </BrowserMockup>
            </div>
          </div>
        )}
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 mt-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-industrial-900 mb-4">
            よくある質問
          </h2>
          <p className="text-slate-600">
            導入前のご不明点はこちらで解消できます
          </p>
        </div>
        <FAQAccordion items={faqItems} />
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-6 mt-20">
        <div className="bg-gradient-to-br from-industrial-900 to-slate-800 rounded-3xl p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            今すぐ無料で始めましょう
          </h2>
          <p className="text-slate-300 mb-8 max-w-lg mx-auto">
            フリープランで機能をお試しください。<br />
            クレジットカード登録不要ですぐにご利用いただけます。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* onClick が無く、押しても何も起きないボタンだった。
                動かないボタンは「壊れている」と受け取られる。 */}
            <button
              onClick={() => onChangeView?.('login')}
              className="px-8 py-4 bg-safety-500 text-white font-bold rounded-xl hover:bg-safety-600 transition shadow-lg shadow-safety-500/30"
            >
              無料で始める
            </button>
            <button
              onClick={() => onChangeView?.('pricing')}
              className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition border border-white/20"
            >
              料金プランを見る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PricingView: React.FC<{ onChangeView: (view: ViewState) => void }> = ({ onChangeView }) => {
  return (
    <div className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-industrial-900 mb-4">現場規模に合わせたシンプル料金</h1>
        <p className="text-xl text-slate-600">初期費用0円。投稿者数・投稿数は無制限です。</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Free */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 flex flex-col self-start shadow-sm">
          <h3 className="text-xl font-bold text-slate-900">Free</h3>
          <div className="mt-4 mb-2">
            <span className="text-4xl font-extrabold tracking-tight text-slate-900">¥0</span>
            <span className="text-slate-500">/月</span>
          </div>
          <p className="text-sm text-slate-600 mb-6">まず試したい方に</p>
          <ul className="space-y-3">
            <li className="flex text-sm text-slate-600"><Check className="h-5 w-5 text-emerald-500 mr-2"/> 管理者 1人</li>
            <li className="flex text-sm text-slate-600"><Check className="h-5 w-5 text-emerald-500 mr-2"/> QRコード 3個</li>
            <li className="flex text-sm text-slate-600"><Check className="h-5 w-5 text-emerald-500 mr-2"/> AI分析 10回/月</li>
            <li className="flex text-sm text-slate-600"><Check className="h-5 w-5 text-emerald-500 mr-2"/> 投稿者数無制限</li>
            <li className="flex text-sm text-slate-600"><Check className="h-5 w-5 text-emerald-500 mr-2"/> 安全掲示ポスター 全27種</li>
          </ul>
          <button onClick={() => onChangeView('login')} className="mt-8 w-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-3 rounded-lg transition">
            無料ではじめる
          </button>
        </div>

        {/* チーム（席数制） */}
        <div className="bg-industrial-900 rounded-2xl p-8 flex flex-col shadow-xl relative">
          <div className="absolute -top-3 right-6">
            <span className="bg-safety-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              現場で使うなら
            </span>
          </div>
          <h3 className="text-xl font-bold text-white">チーム</h3>
          <div className="mt-4 mb-2">
            <span className="text-4xl font-extrabold tracking-tight text-white">¥2,980</span>
            <span className="text-slate-300">/月〜</span>
          </div>
          <p className="text-sm text-slate-300 mb-6">
            1席目 ¥2,980、2席目以降 1席あたり ¥2,480
          </p>

          {/* 席数ごとの料金。人数で決まることを表で示す */}
          <div className="mb-6 rounded-lg bg-white/5 border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 text-xs">
                  <th className="py-2 px-3 text-left font-medium">管理者</th>
                  <th className="py-2 px-3 text-right font-medium">月額</th>
                  <th className="py-2 px-3 text-right font-medium">QR</th>
                  <th className="py-2 px-3 text-right font-medium">AI分析</th>
                </tr>
              </thead>
              <tbody className="text-slate-200">
                {[1, 3, 5, 10].map((n) => (
                  <tr key={n} className="border-t border-white/10">
                    <td className="py-2 px-3">{n}人</td>
                    <td className="py-2 px-3 text-right tabular-nums">¥{priceForSeats(n).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{10 * n}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{(100 * n).toLocaleString()}回</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="space-y-3 flex-1">
            <li className="flex text-sm text-slate-300"><Check className="h-5 w-5 text-safety-500 mr-2"/> 投稿者数無制限</li>
            <li className="flex text-sm text-slate-300"><Check className="h-5 w-5 text-safety-500 mr-2"/> 全現場を横断して集計</li>
            <li className="flex text-sm text-slate-300"><Check className="h-5 w-5 text-safety-500 mr-2"/> 月次レポート（A4 1枚 PDF）</li>
            <li className="flex text-sm text-slate-300"><Check className="h-5 w-5 text-safety-500 mr-2"/> 紙からの取り込み（OCR）</li>
            <li className="flex text-sm text-slate-300"><Check className="h-5 w-5 text-safety-500 mr-2"/> 4Mなぜなぜ深掘り</li>
          </ul>
          <button onClick={() => onChangeView('login')} className="mt-8 w-full bg-safety-500 hover:bg-safety-600 text-white font-bold py-3 rounded-lg transition">
            無料ではじめる
          </button>
          <p className="mt-3 text-xs text-slate-400 text-center">
            まず無料で試して、必要になったら人数を選んでお申し込みいただけます
          </p>
        </div>
      </div>

      <p className="mt-8 text-center text-sm text-slate-600 max-w-3xl mx-auto">
        <strong className="text-industrial-900">「席」は管理画面を使う方の人数です。</strong>
        報告する作業員はアカウント不要・人数無制限でご利用いただけます。<br />
        請求書でのお支払いをご希望の場合や、大人数でのご利用は、お問い合わせください。
      </p>

      {/* 資料DL: プラン比較の検討者を逃さない (社内回覧→上長承認のフローに寄り添う) */}
      <div className="mt-16 max-w-5xl mx-auto">
        <DeckDownloadCTA
          placement="pricing-bottom"
          description="プラン比較・主要機能・セキュリティ要件まで A4 14ページにまとめてあります。上長や社内へのご説明にどうぞ。"
        />
      </div>
    </div>
  );
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const FUNCTIONS_URL = SUPABASE_URL ? `${SUPABASE_URL}/functions/v1` : '';

export const LegalView: React.FC<{ initialTab?: LegalTab }> = ({ initialTab = 'about' }) => {
  const [activeTab, setActiveTab] = React.useState<LegalTab>(initialTab);

  // Contact form state
  const [contactForm, setContactForm] = React.useState({
    name: '',
    email: '',
    company: '',
    inquiryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = React.useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!FUNCTIONS_URL) {
      setSubmitStatus('error');
      setSubmitMessage('サービスが利用できません。メールでお問い合わせください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/genbasafeai_contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify(contactForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'お問い合わせの送信に失敗しました');
      }

      setSubmitStatus('success');
      setSubmitMessage('お問い合わせを受け付けました。確認メールをお送りしましたのでご確認ください。');
      setContactForm({ name: '', email: '', company: '', inquiryType: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(error instanceof Error ? error.message : 'お問い合わせの送信に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'about' as const, label: '運営者情報' },
    { id: 'privacy' as const, label: 'プライバシーポリシー' },
    { id: 'transactions' as const, label: '特定商取引法に基づく表記' },
    { id: 'contact' as const, label: 'お問い合わせ' },
  ];

  return (
    <div className="py-12 max-w-4xl mx-auto px-6">
      {/* Tab Navigation */}
      <div className="border-b border-slate-200 mb-8">
        <nav className="flex flex-wrap -mb-px" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-safety-600 text-safety-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="animate-fade-in">
        {/* 運営者情報 (About) */}
        {activeTab === 'about' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-industrial-900 mb-2">運営者情報</h1>
              <p className="text-slate-500 text-sm">サービス運営に関する情報</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-xl border border-slate-100 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 border-b border-slate-200">
                <span className="font-bold text-slate-800">サービス名</span>
                <span className="sm:col-span-2 text-slate-600">安全ポスト＋（ゲンバセーフAI）</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 border-b border-slate-200">
                <span className="font-bold text-slate-800">運営責任者</span>
                <span className="sm:col-span-2 text-slate-600">國分 良太</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 border-b border-slate-200">
                <span className="font-bold text-slate-800">所在地</span>
                <span className="sm:col-span-2 text-slate-600">ご請求があった場合には速やかに開示いたします</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4">
                <span className="font-bold text-slate-800">お問い合わせ</span>
                <span className="sm:col-span-2 text-slate-600">support@genbacompass.com</span>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold text-industrial-900 mb-4">安全ポスト＋について</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                安全ポスト＋は、建設・製造・物流現場の安全管理をAIで革新するプラットフォームです。
                QRコードを活用した簡単なヒヤリハット報告と、AIによる自動匿名化・分析で、
                現場の安全文化を醸成し、労働災害ゼロの実現を目指します。
              </p>

              <h3 className="text-lg font-bold text-industrial-900 mb-3">提供サービス</h3>
              <ul className="space-y-2 text-slate-600 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>クラウド型安全管理システムの利用権</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>音声・テキスト入力によるヒヤリハット報告機能</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>AIによる自動匿名化・リスク分析機能</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>QRコードによる簡単報告システム</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>統計・分析ダッシュボード機能</span>
                </li>
              </ul>

              <h3 className="text-lg font-bold text-industrial-900 mb-3">ミッション</h3>
              <p className="text-slate-600 leading-relaxed">
                現場の安全課題をAI技術で解決し、「怒られない報告文化」を通じて
                労働災害ゼロの安全な現場づくりに貢献します。
              </p>
            </div>
          </div>
        )}

        {/* プライバシーポリシー */}
        {activeTab === 'privacy' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-industrial-900 mb-2">プライバシーポリシー</h1>
              <p className="text-slate-500 text-sm">最終更新日: 2025年1月1日</p>
            </div>

            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">1. 個人情報の収集</h2>
                <p className="text-slate-600 leading-relaxed">
                  安全ポスト＋（以下「当サービス」）では、以下の個人情報を収集する場合があります：
                </p>
                <ul className="list-disc pl-6 text-slate-600 mt-4 space-y-2">
                  <li>管理者登録時のお名前、メールアドレス、企業情報</li>
                  <li>お問い合わせフォームで入力されたお名前、メールアドレス、お問い合わせ内容</li>
                  <li>アクセス解析ツール（Google Analytics）による匿名化されたアクセス情報</li>
                  <li>Cookie情報（詳細は下記参照）</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">2. 個人情報の利用目的</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  収集した個人情報は、以下の目的で利用します：
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>本サービスの提供および改善</li>
                  <li>お問い合わせへの対応</li>
                  <li>安全管理に関する統計データの作成</li>
                  <li>ウェブサイトのアクセス解析</li>
                  <li>重要なお知らせやサービス情報の提供</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">3. 個人情報の第三者提供</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  当サービスは、以下の場合を除き、個人情報を第三者に提供することはありません：
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>ご本人の同意がある場合</li>
                  <li>法令に基づく場合</li>
                  <li>人の生命、身体または財産の保護のために必要がある場合</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">4. Cookieの使用について</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  当サービスでは、サービスの利便性向上およびアクセス解析のためにCookieを使用しています。
                </p>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                  <p className="text-slate-600 font-medium mb-2">使用しているCookie</p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 text-sm">
                    <li>Google Analytics: アクセス解析のため（匿名化された情報のみ）</li>
                  </ul>
                  <p className="text-slate-500 text-sm mt-3">
                    ブラウザの設定でCookieを無効にすることも可能ですが、一部機能が制限される場合があります。
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">5. アクセス解析ツール</h2>
                <p className="text-slate-600 leading-relaxed">
                  当サービスでは、Google Analyticsを使用してアクセス情報を収集しています。
                  Google Analyticsは、Cookieを使用して匿名でデータを収集します。
                  詳細は
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-safety-600 hover:underline ml-1">
                    Googleプライバシーポリシー
                  </a>
                  をご確認ください。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">6. 個人情報の安全管理</h2>
                <p className="text-slate-600 leading-relaxed">
                  当サービスは、個人情報の漏洩、滅失またはき損の防止その他の個人情報の安全管理のために、
                  適切な安全管理措置を講じます。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">7. 個人情報の開示・訂正・削除</h2>
                <p className="text-slate-600 leading-relaxed">
                  ご本人から個人情報の開示、訂正、削除等の請求があった場合は、
                  ご本人であることを確認の上、合理的な期間内に対応いたします。
                </p>
                <p className="text-slate-600 mt-2">
                  お問い合わせ先: <span className="font-medium">support@genbacompass.com</span>
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-industrial-900 mb-4">8. プライバシーポリシーの変更</h2>
                <p className="text-slate-600 leading-relaxed">
                  当サービスは、必要に応じて本プライバシーポリシーを変更することがあります。
                  変更後のプライバシーポリシーは、本ページに掲載した時点で効力を生じるものとします。
                </p>
              </section>

              <section className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                <h2 className="text-lg font-bold text-industrial-900 mb-4">9. お問い合わせ</h2>
                <p className="text-slate-600 mb-4">
                  本プライバシーポリシーに関するお問い合わせは、以下までご連絡ください。
                </p>
                <div className="space-y-2 text-sm text-slate-600">
                  <p><span className="font-medium">サービス名:</span> 安全ポスト＋</p>
                  <p><span className="font-medium">運営責任者:</span> 國分 良太</p>
                  <p><span className="font-medium">メール:</span> support@genbacompass.com</p>
                </div>
              </section>
            </div>
          </div>
        )}

        {/* 特定商取引法に基づく表記 */}
        {activeTab === 'transactions' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-industrial-900 mb-2">特定商取引法に基づく表記</h1>
              <p className="text-slate-500 text-sm">最終更新日: 2025年1月1日</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-xl border border-slate-100">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">事業者情報</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 border-b border-slate-200">
                  <span className="font-bold text-slate-800">事業者名</span>
                  <span className="sm:col-span-2 text-slate-600">安全ポスト＋</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 border-b border-slate-200">
                  <span className="font-bold text-slate-800">運営責任者</span>
                  <span className="sm:col-span-2 text-slate-600">國分 良太</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 border-b border-slate-200">
                  <span className="font-bold text-slate-800">所在地</span>
                  <span className="sm:col-span-2 text-slate-600">ご請求があった場合には速やかに開示いたします</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 border-b border-slate-200">
                  <span className="font-bold text-slate-800">連絡先</span>
                  <div className="sm:col-span-2 text-slate-600">
                    <p>メールアドレス: support@genbacompass.com</p>
                    <p>電話番号: 080-4190-5754</p>
                    <p className="text-sm text-slate-500 mt-1">（平日昼間は対応できない場合があります。お問い合わせはメールにてお願いいたします）</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4">
                  <span className="font-bold text-slate-800">営業時間</span>
                  <span className="sm:col-span-2 text-slate-600">平日 9:00-18:00（土日祝日を除く）</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">提供サービス</h2>
              <p className="text-slate-600 mb-4">建設・製造・物流現場向けAI安全管理システム「安全ポスト＋」</p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>クラウド型安全管理システムの利用権</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>音声・テキスト認識機能</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>ヒヤリハット報告・管理機能</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>QRコードによる簡単報告機能</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-safety-500 mr-2 mt-0.5" size={18} />
                  <span>統計・分析レポート機能</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">価格・料金</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="font-medium text-slate-800">フリープラン</span>
                  <span className="text-slate-600">無料（QRコード3個まで、AI分析10回/月）</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">チームプラン</span>
                  <span className="text-slate-900 text-right">
                    月額2,980円（税込・管理者1人）<br />
                    管理者を追加する場合は1人につき月額2,480円（税込）を加算
                  </span>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4">※上記価格は全て税込表示です</p>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">支払い方法</h2>
              <p className="text-slate-600 mb-4">クレジットカード決済（Stripe経由）</p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium text-slate-700 mb-2">対応カード:</p>
                <ul className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                  <li>• Visa</li>
                  <li>• MasterCard</li>
                  <li>• American Express</li>
                  <li>• JCB</li>
                  <li>• Diners Club</li>
                </ul>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                ※毎月自動課金となります<br/>
                ※初回課金は登録時、以降は毎月同日に課金されます
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">返品・キャンセルポリシー</h2>
              <p className="text-slate-600 mb-4">
                本サービスはデジタルコンテンツの提供であるため、原則として返品・交換はお受けできません。
              </p>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                <p className="font-medium text-amber-800 mb-2">例外的に対応する場合:</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• システム障害により正常にサービスが提供できなかった場合</li>
                  <li>• 課金システムのエラーによる重複課金が発生した場合</li>
                </ul>
                <p className="text-sm text-amber-700 mt-3">
                  上記の場合は、お問い合わせ先までご連絡ください。個別に対応を検討させていただきます。
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">解約について</h2>
              <div className="space-y-4 text-slate-600">
                <p><span className="font-medium text-slate-800">解約方法:</span> 各サービスのアカウント設定ページから、いつでも解約が可能です。</p>
                <div>
                  <p className="font-medium text-slate-800 mb-2">解約時の注意事項:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>解約時の日割り返金は行っておりません</li>
                    <li>契約期間終了まではサービスをご利用いただけます</li>
                    <li>解約後もデータは一定期間保持されますが、再契約が必要になる場合があります</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">動作環境</h2>
              <div className="space-y-4 text-slate-600">
                <div>
                  <p className="font-medium text-slate-800 mb-2">推奨ブラウザ:</p>
                  <ul className="grid grid-cols-2 gap-2 text-sm">
                    <li>• Google Chrome 90以降</li>
                    <li>• Safari 14以降</li>
                    <li>• Firefox 88以降</li>
                    <li>• Microsoft Edge 90以降</li>
                  </ul>
                </div>
                <p className="text-sm text-slate-500">
                  ※インターネット接続が必要です<br/>
                  ※スマートフォン・タブレットでもご利用いただけます
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-8 rounded-xl border border-slate-100">
              <h2 className="text-xl font-bold text-industrial-900 mb-6">準拠法・管轄裁判所</h2>
              <p className="text-slate-600 mb-4">
                本サービスおよび本表記に関する準拠法は日本法とします。
              </p>
              <p className="text-slate-600">
                本サービスに関する紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
              </p>
            </div>
          </div>
        )}

        {/* お問い合わせ */}
        {activeTab === 'contact' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-industrial-900 mb-2">お問い合わせ</h1>
              <p className="text-slate-500">安全管理に関するご質問、導入のご相談など、お気軽にお問い合わせください。</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* 連絡先情報 */}
              <div className="space-y-6">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <h3 className="font-bold text-industrial-900 mb-4">メールでのお問い合わせ</h3>
                  <p className="text-slate-600 mb-2">
                    <a href="mailto:support@genbacompass.com" className="text-safety-600 hover:underline font-medium">
                      support@genbacompass.com
                    </a>
                  </p>
                  <p className="text-sm text-slate-500">通常2営業日以内にご返信いたします</p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <h3 className="font-bold text-industrial-900 mb-4">お電話でのお問い合わせ</h3>
                  <p className="text-slate-600 mb-2">
                    <a href="tel:080-4190-5754" className="text-safety-600 hover:underline font-medium">
                      080-4190-5754
                    </a>
                  </p>
                  <p className="text-sm text-slate-500">
                    平日昼間は対応できない場合があります。<br/>
                    お問い合わせはメールにてお願いいたします。
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl border border-slate-200">
                  <h3 className="font-bold text-industrial-900 mb-4">迅速な回答をお約束</h3>
                  <p className="text-slate-600">
                    お問い合わせいただいた内容については、営業日2日以内にご回答いたします。
                  </p>
                </div>
              </div>

              {/* お問い合わせフォーム */}
              <div className="bg-white p-8 rounded-xl border border-slate-200">
                <h3 className="font-bold text-industrial-900 mb-6">お問い合わせフォーム</h3>

                {submitStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="text-green-600" size={32} />
                    </div>
                    <h4 className="text-lg font-bold text-industrial-900 mb-2">送信完了</h4>
                    <p className="text-slate-600 mb-6">{submitMessage}</p>
                    <button
                      onClick={() => setSubmitStatus('idle')}
                      className="text-safety-600 hover:underline font-medium"
                    >
                      新しいお問い合わせを送信
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {submitMessage}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent disabled:bg-slate-100"
                        placeholder="山田 太郎"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent disabled:bg-slate-100"
                        placeholder="example@company.co.jp"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        会社名・組織名
                      </label>
                      <input
                        type="text"
                        value={contactForm.company}
                        onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent disabled:bg-slate-100"
                        placeholder="株式会社〇〇"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        お問い合わせ種別 <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={contactForm.inquiryType}
                        onChange={(e) => setContactForm(prev => ({ ...prev, inquiryType: e.target.value }))}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent disabled:bg-slate-100"
                      >
                        <option value="">選択してください</option>
                        <option value="product">安全ポスト＋についてのご質問</option>
                        <option value="trial">無料トライアルのお申し込み</option>
                        <option value="demo">デモのご依頼</option>
                        <option value="enterprise">企業向けプランのご相談</option>
                        <option value="safety">現場の安全対策相談</option>
                        <option value="pricing">料金についてのご質問</option>
                        <option value="support">技術サポート</option>
                        <option value="other">その他</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        お問い合わせ内容 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        required
                        rows={5}
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent disabled:bg-slate-100"
                        placeholder="お問い合わせ内容をご記入ください"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 px-4 bg-safety-600 text-white font-bold rounded-lg hover:bg-safety-700 transition shadow-lg shadow-safety-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          送信中...
                        </>
                      ) : (
                        '送信する'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
